import classNames from 'classnames';
import Image from 'next/image';
import { Image as ImageProps } from 'src/types/Image';
import { Button } from '@get-mosh/msh-shared';
import styles from './hero.module.scss';
import { Vertical, Subvertical } from 'src/constants/Verticals';
import HTMLReactParser from 'html-react-parser';
import { EventProps } from 'src/analytics/EventProps';
import { getMediaBreakpointValue } from 'src/helpers/getBreakpointValues';
import { Screen } from 'src/types/Screen';
import Heading from '../Heading/Heading';

export type HeroBannerProps = {
    image?: ImageProps;
    heading: string;
    headingTag?: 'h1' | 'h2';
    description?: string;
    cta?: {
        label: string;
        link: string;
        trackEventProps?: EventProps;
        trackEvent?: () => void;
        external?: boolean;
    };
    vertical?: Vertical | Subvertical | undefined;
    className?: string;
    children?: React.ReactNode;
};

const HeroBanner = ({ headingTag = 'h1', ...props }: HeroBannerProps) => {
    return (
        <div
            className={classNames(
                'hero-generic hero-banner',
                styles['section'],
            )}
            data-hero
        >
            {props.image && (
                <picture>
                    {props.image.responsive &&
                        props.image.responsive.map((e, i) => (
                            <source
                                key={i}
                                srcSet={e.src}
                                width={e.width}
                                height={e.height}
                                media={getMediaBreakpointValue(
                                    e.screen as Screen,
                                )}
                                type={e.format}
                            />
                        ))}
                    <Image
                        src={props.image.src}
                        width={props.image.width}
                        height={props.image.height}
                        alt={props.heading || ''}
                        priority
                        sizes={
                            (!props.image.responsive && '100vw') || undefined
                        }
                    />
                </picture>
            )}
            <div className="container mx-auto md:relative md:w-full">
                <div className={classNames(styles['content'], props.className)}>
                    <Heading level={headingTag} tag="h">
                        {HTMLReactParser(props.heading)}
                    </Heading>
                    {props.description && (
                        <p className={`${styles['description']}`}>
                            {props.description}
                        </p>
                    )}
                    {props.children && (
                        <div className={styles['children']}>
                            {props.children}
                        </div>
                    )}
                    {props.cta && (
                        <Button
                            as="a"
                            text={props.cta.label}
                            href={props.cta.link}
                            target={props.cta.external ? '_blank' : '_self'}
                            onClick={props.cta.trackEvent}
                            stretch
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default HeroBanner;
